/**
 * Employee Delete Validation Service
 *
 * Pure domain service for validating employee deletion.
 * Contains business rules for when an employee can or cannot be deleted.
 *
 * @module personnel/domain/services/employeeDeleteValidation
 */

import { INSTALL_PERMISSIONS, UNDELETABLE_ROLES } from '@personnel/domain/constants';

/**
 * Minimal employee data needed for delete validation
 */
export interface EmployeeDeleteCandidate {
  uuid: string;
  roleName?: string | null;
  permissions?: ReadonlyArray<string>;
}

/**
 * Result of delete validation
 */
export type DeleteValidationResult = { canDelete: true } | { canDelete: false; reason: 'self_user' | 'install_user' };

/**
 * Check if an employee has install permissions (POS/Terminal access)
 *
 * @param employee - Employee to check
 * @returns true if employee has install permissions
 */
export const hasInstallPermissions = (employee: EmployeeDeleteCandidate): boolean => {
  return employee.permissions?.some((permission) => INSTALL_PERMISSIONS.includes(permission as any)) ?? false;
};

/**
 * Check if an employee has an undeletable role
 *
 * @param employee - Employee to check
 * @returns true if employee has an undeletable role
 */
export const hasUndeletableRole = (employee: EmployeeDeleteCandidate): boolean => {
  if (!employee.roleName) return false;
  return UNDELETABLE_ROLES.includes(employee.roleName as any);
};

/**
 * Validate if an employee can be deleted
 *
 * Business rules:
 * 1. User cannot delete themselves
 * 2. Install users (POS/Terminal access) cannot be deleted
 *
 * Note: Super Admin deletion is validated by the backend (returns 400 error)
 *
 * @param employee - Employee to validate
 * @param currentUserUuid - UUID of the current logged-in user
 * @returns Validation result with reason if cannot delete
 *
 * @example
 * const result = validateEmployeeDelete(employee, currentUserUuid);
 * if (!result.canDelete) {
 *   showDialog(result.reason);
 * }
 */
export const validateEmployeeDelete = (
  employee: EmployeeDeleteCandidate,
  currentUserUuid: string,
): DeleteValidationResult => {
  // Rule 1: Cannot delete self
  if (employee.uuid === currentUserUuid) {
    return { canDelete: false, reason: 'self_user' };
  }

  // Rule 2: Cannot delete install users
  if (hasInstallPermissions(employee)) {
    return { canDelete: false, reason: 'install_user' };
  }

  return { canDelete: true };
};

/**
 * Filter a list of employees to only those that can be deleted
 *
 * @param employees - List of employees to filter
 * @param currentUserUuid - UUID of the current logged-in user
 * @returns Array of employees that can be deleted
 *
 * @example
 * const deletableEmployees = filterDeletableEmployees(selectedEmployees, currentUserUuid);
 * await bulkDelete(deletableEmployees.map(e => e.uuid));
 */
export const filterDeletableEmployees = <T extends EmployeeDeleteCandidate>(
  employees: ReadonlyArray<T>,
  currentUserUuid: string,
): T[] => {
  return employees.filter((employee) => {
    const result = validateEmployeeDelete(employee, currentUserUuid);
    return result.canDelete;
  });
};
